import {
  BenchmarkCatalogClient,
  BenchmarkCatalogCreateRequest,
  BenchmarkCatalogDto,
  BenchmarkCatalogSummaryDto,
  BenchmarkCatalogUpdateRequest,
  BenchmarkCatalogValueInput,
  ProjectBenchmarkAndTargetsDto,
  ProjectTargetDto,
} from "@api/apiClient";
import axios from "@api/axiosInstance";
import { queryClient } from "@hooks/useGlobalState";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

// ── Client singleton ──────────────────────────────────────────────────────────

const catalogClient = new BenchmarkCatalogClient(undefined, axios);

// ── Type re-exports (keep consuming code backward-compatible) ─────────────────

export type { BenchmarkCatalogDto, BenchmarkCatalogValueInput, ProjectTargetDto };
/** @deprecated prefer BenchmarkCatalogSummaryDto from @api/apiClient */
export type BenchmarkCatalogSummary = BenchmarkCatalogSummaryDto;

// ── Query Keys ─────────────────────────────────────────────────────────────────

export const BENCHMARK_CATALOGS_KEY = ["benchmarkCatalogs"] as const;
export const benchmarkCatalogKey = (id: string) =>
  ["benchmarkCatalog", id] as const;
export const benchmarkAndTargetsKey = (projectId: string) =>
  ["benchmarkAndTargets", projectId] as const;

// ── Queries ────────────────────────────────────────────────────────────────────

/** Lightweight list for dropdowns (id + name + description only). */
export function useBenchmarkCatalogSummaries(options?: { enabled?: boolean }) {
  return useQuery<BenchmarkCatalogSummaryDto[]>({
    enabled: options?.enabled ?? true,
    queryKey: BENCHMARK_CATALOGS_KEY,
    queryFn: () => catalogClient.getAllSummaries(),
    placeholderData: [],
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

/**
 * Fetches ALL catalogs with their full value rows in parallel.
 * Used by the MetaData Management admin grid to render the
 * BenchmarkCatalog → BenchmarkCatalogValue hierarchy.
 *
 * Reuses per-catalog query keys so values are cached individually and shared
 * with the edit dialog.
 */
export function useAllBenchmarkCatalogsWithValues(options?: {
  enabled?: boolean;
}) {
  const isEnabled = options?.enabled ?? true;

  const summariesQuery = useQuery<BenchmarkCatalogSummaryDto[]>({
    enabled: isEnabled,
    queryKey: BENCHMARK_CATALOGS_KEY,
    queryFn: () => catalogClient.getAllSummaries(),
    placeholderData: [],
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

  const summaries = summariesQuery.data ?? [];

  const detailQueries = useQueries({
    queries: summaries.map((s) => ({
      queryKey: benchmarkCatalogKey(s.metadataEntityId ?? ""),
      queryFn: () => catalogClient.getById(s.metadataEntityId!),
      enabled: isEnabled && !!s.metadataEntityId,
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
    })),
  });

  // Memoize by each query's dataUpdatedAt timestamp so the returned array
  // reference is stable between renders when data has not actually changed.
  // Without this, .map().filter() creates a new array reference on every render,
  // causing MetaDataGrid's useEffect([materials]) to fire continuously and
  // prevent the expand/collapse state from taking effect.
  const dataUpdatedKey = detailQueries
    .map((q) => q.dataUpdatedAt ?? 0)
    .join("-");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = useMemo(
    () =>
      detailQueries
        .map((q) => q.data)
        .filter((d): d is BenchmarkCatalogDto => d != null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataUpdatedKey]
  );

  return {
    data,
    isFetching:
      summariesQuery.isFetching || detailQueries.some((q) => q.isFetching),
    isPending:
      summariesQuery.isPending || detailQueries.some((q) => q.isPending),
    refetch: () => {
      summariesQuery.refetch();
      detailQueries.forEach((q) => q.refetch());
    },
  };
}

/**
 * Full catalog with all value rows (including [NEW] gaps for recently added
 * model types). Used by the admin dialog and the project settings read-only view.
 */
export function useBenchmarkCatalog(
  id: string | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<BenchmarkCatalogDto | null>({
    enabled: !!id && (options?.enabled ?? true),
    queryKey: benchmarkCatalogKey(id ?? ""),
    queryFn: () => catalogClient.getById(id!),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

/** Benchmark values (from linked catalog) and target values for a project. */
export function useBenchmarkAndTargets(
  projectId: string | null | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<ProjectBenchmarkAndTargetsDto>({
    enabled: !!projectId && (options?.enabled ?? true),
    queryKey: benchmarkAndTargetsKey(projectId ?? ""),
    queryFn: () =>
      catalogClient.getBenchmarkAndTargetsForProject(projectId!),
    placeholderData: undefined,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export function useCreateBenchmarkCatalog(options?: {
  onSuccess?: (data: BenchmarkCatalogDto) => void;
  onError?: (err: unknown) => void;
}) {
  return useMutation<BenchmarkCatalogDto, unknown, BenchmarkCatalogCreateRequest>({
    mutationFn: (request) => catalogClient.create(request),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: BENCHMARK_CATALOGS_KEY });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useUpdateBenchmarkCatalog(options?: {
  onSuccess?: (data: BenchmarkCatalogDto) => void;
  onError?: (err: unknown) => void;
}) {
  return useMutation<
    BenchmarkCatalogDto,
    unknown,
    { id: string; request: BenchmarkCatalogUpdateRequest }
  >({
    mutationFn: ({ id, request }) => catalogClient.update(id, request),
    onSuccess: async (data, { id }) => {
      await queryClient.invalidateQueries({ queryKey: BENCHMARK_CATALOGS_KEY });
      queryClient.removeQueries({ queryKey: benchmarkCatalogKey(id) });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeleteBenchmarkCatalog(options?: {
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
}) {
  return useMutation<void, unknown, string>({
    mutationFn: (id) => catalogClient.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: BENCHMARK_CATALOGS_KEY });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}
